import type { StudentSummary } from '@/services/api/student-summary';
import { fetchApi } from '@/services/api-client';

export const ADMIN_STUDENT_ABSENCES_PATH = '/admin/student-absences';

export type StudentAbsenceType = 'sick' | 'family' | 'travel' | 'medical' | 'other';

export type StudentAbsence = {
  id: string;
  studentId: string;
  student: StudentSummary | null;
  submittedByParentId: string | null;
  submittedByParentName: string | null;
  type: StudentAbsenceType;
  startDate: string;
  endDate: string;
  reason: string;
  /** Who put the absence on file, and when — null when a family reported it themselves. */
  reviewerId: string | null;
  reviewedAt: string | null;
  createdAt: string;
};

export type AdminCreateStudentAbsenceData = {
  studentId: string;
  type: StudentAbsenceType;
  startDate: string;
  endDate: string;
  reason: string;
};

/**
 * A correction to an absence already on file. Only what moved is sent, and the child is not
 * among the fields — an absence that changes child is a different absence.
 */
export type UpdateStudentAbsenceData = Partial<Omit<AdminCreateStudentAbsenceData, 'studentId'>>;

export const adminStudentAbsencesApi = {
  list: (params: { studentId?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.studentId) qs.append('studentId', params.studentId);
    const s = qs.toString();
    return fetchApi<StudentAbsence[]>(`${ADMIN_STUDENT_ABSENCES_PATH}${s ? `?${s}` : ''}`);
  },
  detail: (id: string) => fetchApi<StudentAbsence>(`${ADMIN_STUDENT_ABSENCES_PATH}/${id}`),
  update: (id: string, data: UpdateStudentAbsenceData) =>
    fetchApi<StudentAbsence>(`${ADMIN_STUDENT_ABSENCES_PATH}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  createOnBehalf: (data: AdminCreateStudentAbsenceData) =>
    fetchApi<StudentAbsence>(ADMIN_STUDENT_ABSENCES_PATH, { method: 'POST', body: JSON.stringify(data) }),
  // The undo for any absence, whoever reported it: nothing waits to be approved.
  remove: (id: string) => fetchApi<void>(`${ADMIN_STUDENT_ABSENCES_PATH}/${id}`, { method: 'DELETE' }),
};
